import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DocumentReferencesView } from 'api';
import { CAP_TABLE_REFERENCE_TITLE } from 'common/constants/documents';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useLayoutContext } from 'context/LayoutContext';
import { useDocuments } from 'services/hooks';

export const CREATE_CITATIONS_LOADING_TOOLTIP = 'Loading cap table document references...';
export const CREATE_CITATIONS_NO_SECURITIES_TOOLTIP = 'This cap table has no securities to cite.';
export const CREATE_CITATIONS_NO_DOCUMENTS_TOOLTIP = 'This cap table has no referenced documents to cite against.';

interface UseCreateCapTableCitationsActionParams {
  enabled: boolean;
}

export interface CreateCapTableCitationsAction {
  closeDialog: () => void;
  disabledReason: string | null;
  documents: DocumentReferencesView[];
  isDialogOpen: boolean;
  openDialog: () => void;
}

/**
 * Guard + data source for the cap-table "Create Citations" page action.
 * Eagerly fetches the cap table's document references (the same endpoint and
 * filter the documents widget uses) so the kebab item can disable itself with
 * an explanatory tooltip; the fetched list also feeds the confirm dialog.
 */
const useCreateCapTableCitationsAction = ({
  enabled,
}: UseCreateCapTableCitationsActionParams): CreateCapTableCitationsAction => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const { currentMeasurementDate } = useLayoutContext();
  const { foundReferencedDocuments, getReferencesByMeasurementDateId } = useDocuments() as unknown as {
    foundReferencedDocuments: (
      docs: DocumentReferencesView[] | null,
      currentPage: string,
      referencedFeatureId: string | number
    ) => DocumentReferencesView[];
    getReferencesByMeasurementDateId: (measurementDateId: string | number) => Promise<unknown>;
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [measurementDateDocuments, setMeasurementDateDocuments] = useState<DocumentReferencesView[] | null>(null);

  const capTableId = captableInfo?.id;
  const measurementDateId = currentMeasurementDate?.id;

  useEffect(() => {
    if (!enabled || !measurementDateId) {
      setMeasurementDateDocuments(null);
      return undefined;
    }

    let isCancelled = false;
    (async () => {
      const references = await getReferencesByMeasurementDateId(measurementDateId);
      if (!isCancelled) {
        // The documents service resolves with the error object on failure, so
        // anything non-array means "no usable references".
        setMeasurementDateDocuments(Array.isArray(references) ? (references as DocumentReferencesView[]) : []);
      }
    })();

    return () => {
      isCancelled = true;
    };
    // useDocuments re-creates its functions every render; mirror the widget and key the fetch by its inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, measurementDateId]);

  const documents = useMemo(
    () =>
      capTableId && measurementDateDocuments
        ? foundReferencedDocuments(measurementDateDocuments, CAP_TABLE_REFERENCE_TITLE, capTableId)
        : [],
    // foundReferencedDocuments is re-created by useDocuments on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capTableId, measurementDateDocuments]
  );

  const hasCitableSecurities = (captableInfo?.securities ?? []).some(
    security => !security.is_deleted && !security.is_hidden
  );

  // Each guard surfaces as soon as it is decidable; until both the cap table
  // and its references have loaded the item stays optimistically disabled.
  let disabledReason: string | null = null;
  if (capTableId && !hasCitableSecurities) {
    disabledReason = CREATE_CITATIONS_NO_SECURITIES_TOOLTIP;
  } else if (!capTableId || measurementDateDocuments === null) {
    disabledReason = CREATE_CITATIONS_LOADING_TOOLTIP;
  } else if (documents.length === 0) {
    disabledReason = CREATE_CITATIONS_NO_DOCUMENTS_TOOLTIP;
  }

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  return { closeDialog, disabledReason, documents, isDialogOpen, openDialog };
};

export default useCreateCapTableCitationsAction;
